import React, { useState, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Styles from "./ArchiveModal.module.css";
import { ReactComponent as HistoryIcon } from "../../../../assets/images/history.svg";
import { ReactComponent as VectorIcon } from "../../../../assets/images/VectorIcon.svg";
import TermsConditionModal from "../TermsConditionModal/TermsConditionModal";
import UploadDocModal from "../UploadDocModal/UploadDocModal";
import ViewDoc from "../ViewDoc/ViewDoc";
import LedgerHistory from "../LedgerHistory/LedgerHistory";
import ViewInjectionModal from "../ViewInjectionModal/ViewInjectionModal";
import ViewDrawalModal from "../ViewDrawalModal/ViewDrawalModal";
import UpdateLedger from "../UpdateLedger/UpdateLedger";
import ConnectionHistory from "../ConnectionHistory/ConnectionHistory";
import ViewInjectDrawal from "../ViewInjectDrawal/ViewInjectDrawal";
import { openBilateralChat } from "../CreateEdit/helpers";
import { fetchArchiveConnectionList } from "../../../../Redux/Slices/bankingSlice";

const TABS = [
  { label: "Completed", key: "Completed", status: "PAST" },
  { label: "Placed", key: "Placed", status: "PLACED" },
  { label: "Received", key: "Received", status: "RECEIVED" },
];

const NO_RECORD_MESSAGES = {
  Completed: "No completed records found.",
  Placed: "No placed records found.",
  Received: "No received records found.",
};

const ArchiveModal = ({
  data,
  isLoading,
  onRefresh,
  onOngoingStatusDetected,
  isExpiredView,
  isWithdrawanView,
  isInClosedView,
}) => {
  const dispatch = useDispatch();
  const selfListingId = data[0]?.selfListing?.id;
  const visibleTabs = useMemo(() => {
    if ((isExpiredView || isWithdrawanView) && !isInClosedView) {
      return TABS.filter((tab) => tab.key !== "Completed");
    }
    return TABS;
  }, [isExpiredView, isWithdrawanView, isInClosedView]);

  const [activeTab, setActiveTab] = useState(() => {
    if (isInClosedView) {
      return "Completed";
    }
    if (isExpiredView || isWithdrawanView) {
      return "Placed";
    }
    return "Completed";
  });
  const [finalModal, setFinalModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [selectedCounterparty, setSelectedCounterparty] = useState(null);
  const [acceptedConnections, setAcceptedConnections] = useState([]);
  const [modalViewOnly, setModalViewOnly] = useState(false);
  const [modalApprovalMode, setModalApprovalMode] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [viewDocModalOpen, setViewDocModalOpen] = useState(false);
  const [viewDocConnectionId, setViewDocConnectionId] = useState(null);
  const [activeViewCounterparty, setActiveViewCounterparty] = useState(null);
  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const [ledgerConnectionId, setLedgerConnectionId] = useState(null);
  const [viewInjectionModalOpen, setViewInjectionModalOpen] = useState(false);
  const [selectedInjectionData, setSelectedInjectionData] = useState([]);
  const [viewDrawalModalOpen, setViewDrawalModalOpen] = useState(false);
  const [selectedDrawalData, setSelectedDrawalData] = useState([]);
  const [updateLedgerModalOpen, setUpdateLedgerModalOpen] = useState(false);
  const [updateLedgerConnectionId, setUpdateLedgerConnectionId] =
    useState(null);
  const [connectionHistoryOpen, setConnectionHistoryOpen] = useState(false);
  const [historyConnectionId, setHistoryConnectionId] = useState(null);
  const [viewDetails, setViewDetails] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [hasPlacedData, setHasPlacedData] = useState(false);
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const [isInitialTabSet, setIsInitialTabSet] = useState(false);

  const [placedData, setPlacedData] = useState([]);
  const [receivedData, setReceivedData] = useState([]);
  const [completedData, setCompletedData] = useState([]);

  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  )?.toLowerCase();

  const filteredData = useMemo(() => {
    if (!Array.isArray(data))
      return { Completed: [], Placed: [], Received: [] };

    return {
      Completed: data.filter((bid) => bid?.selfListing?.status === "DELIVERED"),
      Placed: data.filter((bid) => bid?.selfListing?.status === "PLACED"),
      Received: data.filter((bid) => bid?.selfListing?.status === "RECEIVED"),
    };
  }, [data]);

  const userData = useSelector((state) => state.persistedReducer);
  const userId = userData?.user?.user?.userId;

  const loading = useSelector((state) => state.bankingSlice.loading);

  const isPlacedTab = activeTab === "Placed" || activeTab === "Received";

  const transformArchiveData = (connections) => {
    if (!Array.isArray(connections)) return [];

    const uniqueConnectionsMap = new Map();

    connections.forEach((connection) => {
      const againstListingId = connection.againstBankingListing?.id;

      if (!againstListingId) return;

      if (!uniqueConnectionsMap.has(againstListingId)) {
        uniqueConnectionsMap.set(againstListingId, connection);
      }
    });

    return Array.from(uniqueConnectionsMap.values()).map((connection) => ({
      selfListing: connection.bankingListing,
      counterPartyListings: [
        {
          ...connection.againstBankingListing,
          connectionId: connection.id,
          status: connection.status,
          agreementStatus: connection.agreementStatus,
          connectionInitiatorListingId: connection.connectionInitiatorId,
          finalizedConnectionValues: connection.finalizedConnectionValues,
          ledgers: connection.ledgers || [],
          loaDocumentHistory: connection.loaDocumentHistory || [],
          loiDocumentHistory: connection.loiDocumentHistory || [],
          agreementDocumentHistory: connection.agreementDocumentHistory || [],
        },
      ],
    }));
  };

  const currentRows = useMemo(() => {
    if (activeTab === "Completed") {
      return transformArchiveData(completedData);
    } else if (activeTab === "Placed") {
      return transformArchiveData(placedData);
    } else if (activeTab === "Received") {
      return transformArchiveData(receivedData);
    }
    return [];
  }, [activeTab, completedData, placedData, receivedData]);

  const shouldShowAlreadyAcceptedMessage = (
    selectedConnection,
    selectedCounterparty,
  ) => {
    if (
      !selectedConnection?.counterPartyListings ||
      !Array.isArray(selectedConnection.counterPartyListings)
    ) {
      return false;
    }
    return (
      selectedConnection.counterPartyListings.some(
        (cp) => cp.status === "TERMS_ACCEPTED" || cp.status === "COMPLETED",
      ) && selectedCounterparty?.status !== "ACTIVE"
    );
  };

  const finalAgreementFile = {
    name: selectedConnection?.agreementDocumentName || "",
    url: selectedConnection?.agreementDocumentUrl || "",
  };

  const agreementStatus = selectedCounterparty?.agreementStatus || "";

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handleCloseModal = () => setFinalModal(false);

  const handleAccepted = (id) => {
    setAcceptedConnections((prev) => [...prev, id]);
    handleCloseModal();
  };

  const handleDeclined = (id) => {
    setAcceptedConnections((prev) => [...prev, id]);
    handleCloseModal();
  };

  const handleUploadClose = () => {
    setUploadModal(false);
  };

  const handleAllDocsUploaded = () => {
    handleUploadClose();
  };

  const handleCloseViewDoc = () => {
    setViewDocModalOpen(false);
    setViewDocConnectionId(null);
  };

  const handleCloseLedger = () => {
    setLedgerModalOpen(false);
    setLedgerConnectionId(null);
  };

  const handleCloseUpdateLedger = () => {
    setUpdateLedgerModalOpen(false);
    setUpdateLedgerConnectionId(null);
  };

  const handleCloseConnectionHistory = () => {
    setHistoryConnectionId(null);
    setConnectionHistoryOpen(false);
  };

  const handleCloseView = () => {
    setViewDetails(false);
  };

  const handleOpenView = (listing) => {
    setSelectedListing(listing);
    setViewDetails(true);
  };

  const handleChatOpen = (fromUser, toUser, initId) => {
    let chatId;

    if (fromUser === initId) {
      chatId = `${fromUser?.split("_")?.[0]}_${toUser?.split("_")?.[0]}`;
    } else {
      chatId = `${toUser?.split("_")?.[0]}_${fromUser?.split("_")?.[0]}`;
    }

    openBilateralChat(dispatch, {
      header: toUser,
      fromUser,
      toUser,
      chatId,
    });
  };

  useEffect(() => {
    if ((isExpiredView || isWithdrawanView) && !isInClosedView) {
      if (activeTab === "Completed") {
        setActiveTab("Placed");
      }
    }
  }, [isExpiredView, isWithdrawanView, isInClosedView, activeTab]);

  useEffect(() => {
    const checkInitialData = async () => {
      if (isInClosedView && selfListingId && !initialLoadComplete) {
        try {
          const completedResult = await dispatch(
            fetchArchiveConnectionList({
              listingId: selfListingId,
              status: "DELIVERED",
              page: 0,
              size: 20,
            }),
          ).unwrap();
          setCompletedData(completedResult || []);
          setInitialLoadComplete(true);
          setIsInitialTabSet(true);
        } catch (error) {
          console.error("Error fetching completed data:", error);
          setInitialLoadComplete(true);
          setIsInitialTabSet(true);
        }
      } else if (
        (isExpiredView || isWithdrawanView) &&
        selfListingId &&
        !initialLoadComplete
      ) {
        try {
          const placedResult = await dispatch(
            fetchArchiveConnectionList({
              listingId: selfListingId,
              status: "PLACED",
              page: 0,
              size: 20,
            }),
          ).unwrap();

          const placedHasData =
            placedResult &&
            Array.isArray(placedResult) &&
            placedResult.length > 0;
          setHasPlacedData(placedHasData);
          setPlacedData(placedResult || []);

          const receivedResult = await dispatch(
            fetchArchiveConnectionList({
              listingId: selfListingId,
              status: "RECEIVED",
              page: 0,
              size: 20,
            }),
          ).unwrap();

          const receivedHasData =
            receivedResult &&
            Array.isArray(receivedResult) &&
            receivedResult.length > 0;
          setHasReceivedData(receivedHasData);
          setReceivedData(receivedResult || []);
          if (placedHasData) {
            setActiveTab("Placed");
          } else if (receivedHasData) {
            setActiveTab("Received");
          } else {
            setActiveTab("Placed");
          }

          setInitialLoadComplete(true);
          setIsInitialTabSet(true);
        } catch (error) {
          console.error("Error checking initial data:", error);
          setActiveTab("Placed");
          setInitialLoadComplete(true);
          setIsInitialTabSet(true);
        }
      } else if (
        !isExpiredView &&
        !isWithdrawanView &&
        !isInClosedView &&
        selfListingId &&
        !initialLoadComplete
      ) {
        if (activeTab === "Completed") {
          try {
            const completedResult = await dispatch(
              fetchArchiveConnectionList({
                listingId: selfListingId,
                status: "DELIVERED",
                page: 0,
                size: 20,
              }),
            ).unwrap();
            setCompletedData(completedResult || []);
          } catch (error) {
            console.error("Error fetching completed data:", error);
          }
        }
        setInitialLoadComplete(true);
        setIsInitialTabSet(true);
      }
    };

    checkInitialData();
  }, [
    selfListingId,
    isExpiredView,
    isWithdrawanView,
    isInClosedView,
    dispatch,
    initialLoadComplete,
    activeTab,
  ]);

  useEffect(() => {
    if (!selfListingId || !initialLoadComplete || !isInitialTabSet) return;

    const fetchTabData = async () => {
      try {
        if (activeTab === "Completed") {
          if (completedData.length === 0) {
            const result = await dispatch(
              fetchArchiveConnectionList({
                listingId: selfListingId,
                status: "DELIVERED",
                page: 0,
                size: 20,
              }),
            ).unwrap();
            setCompletedData(result || []);
          }
        } else if (activeTab === "Placed") {
          const result = await dispatch(
            fetchArchiveConnectionList({
              listingId: selfListingId,
              status: "PLACED",
              page: 0,
              size: 20,
            }),
          ).unwrap();
          setPlacedData(result || []);
        } else if (activeTab === "Received") {
          const result = await dispatch(
            fetchArchiveConnectionList({
              listingId: selfListingId,
              status: "RECEIVED",
              page: 0,
              size: 20,
            }),
          ).unwrap();
          setReceivedData(result || []);
        }
      } catch (error) {
        console.error("Error fetching tab data:", error);
      }
    };

    fetchTabData();
  }, [
    activeTab,
    selfListingId,
    dispatch,
    initialLoadComplete,
    isInitialTabSet,
    isExpiredView,
    isWithdrawanView,
    isInClosedView,
    completedData.length,
  ]);

  const getNoRecordsMessage = () => {
    if (isExpiredView || isWithdrawanView) {
      if (!hasPlacedData && !hasReceivedData) {
        return "No matching records found.";
      }
    }
    return NO_RECORD_MESSAGES[activeTab];
  };

  return (
    <>
      {finalModal && (
        <TermsConditionModal
          activeData={selectedConnection}
          activeCounterparty={selectedCounterparty}
          open={finalModal}
          handleClose={handleCloseModal}
          connectionId={selectedConnection?.id}
          counterpartyId={selectedCounterparty?.id}
          onAccept={() => {
            handleAccepted(selectedCounterparty?.id);
            if (onRefresh) onRefresh();
          }}
          onDecline={() => {
            handleDeclined(selectedCounterparty?.id);
            if (onRefresh) onRefresh();
          }}
          termsData={data}
          viewOnly={modalViewOnly}
          isApprovalMode={modalApprovalMode}
          showAlreadyAcceptedMessage={shouldShowAlreadyAcceptedMessage(
            selectedConnection,
            selectedCounterparty,
          )}
        />
      )}
      {uploadModal && (
        <UploadDocModal
          open={uploadModal}
          handleClose={() => {
            handleUploadClose();
            if (onRefresh) onRefresh();
          }}
          connectionId={
            selectedCounterparty?.connectionId || selectedConnection?.id
          }
          activeCounterparty={selectedCounterparty}
          onAllDocsUploaded={() => {
            handleAllDocsUploaded();
            if (onRefresh) onRefresh();
          }}
          upData={data}
          agreementStatus={agreementStatus}
          finalAgreementFile={finalAgreementFile}
          isCounterparty={
            !!selectedCounterparty?.agreementInitiatorId &&
            selectedCounterparty?.agreementInitiatorId !== userId
          }
          onRefresh={onRefresh}
        />
      )}

      {viewDocModalOpen && (
        <ViewDoc
          open={viewDocModalOpen}
          handleClose={handleCloseViewDoc}
          connectionId={viewDocConnectionId}
          viewDocData={data}
          activeCounterparty={activeViewCounterparty}
        />
      )}

      {ledgerModalOpen && (
        <LedgerHistory
          open={ledgerModalOpen}
          handleClose={handleCloseLedger}
          connectionId={ledgerConnectionId}
          viewDocData={data}
          activeCounterparty={activeViewCounterparty}
        />
      )}

      {viewInjectionModalOpen && (
        <ViewInjectionModal
          open={viewInjectionModalOpen}
          handleClose={() => setViewInjectionModalOpen(false)}
          data={selectedInjectionData}
        />
      )}

      {viewDrawalModalOpen && (
        <ViewDrawalModal
          open={viewDrawalModalOpen}
          handleClose={() => setViewDrawalModalOpen(false)}
          data={selectedDrawalData}
        />
      )}

      {viewDetails && (
        <ViewInjectDrawal
          open={viewDetails}
          handleClose={handleCloseView}
          data={selectedListing}
        />
      )}

      {updateLedgerModalOpen && (
        <UpdateLedger
          open={updateLedgerModalOpen}
          handleClose={() => {
            handleCloseUpdateLedger();
            if (onRefresh) onRefresh();
          }}
          connectionData={updateLedgerConnectionId}
          listingId={selfListingId}
          ongoingData={data}
          currentPage={1}
          itemsPerPage={10}
          totalCount={0}
          onPageChange={() => {}}
          onLedgerUpdated={() => {
            if (onRefresh) onRefresh();
          }}
        />
      )}

      {connectionHistoryOpen && (
        <ConnectionHistory
          connectionId={historyConnectionId}
          open={connectionHistoryOpen}
          handleClose={handleCloseConnectionHistory}
        />
      )}

      <div className={Styles.accordionOpenMain}>
        {/* Left Sidebar - Tabs */}
        <div className={Styles.archiveLeft}>
          <div className={Styles.leftModal}>
            {visibleTabs.map((tab, index) => (
              <div
                key={tab.key}
                className={`${Styles.tabItem} ${
                  activeTab === tab.key ? Styles.activeTab : Styles.inactiveTab
                } ${index === 0 && activeTab === tab.key ? Styles.firstTabActive : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </div>
            ))}
          </div>
        </div>

        {/* Right Content Area */}
        <div className={Styles.archiveRight}>
          {/* Table Header */}
          <table
            className={Styles.archiveTable}
            style={{ tableLayout: "fixed", width: "100%" }}
          >
            <colgroup>
              <col style={{ width: "20%" }} />
              <col style={{ width: "25%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "15%" }} />
            </colgroup>
            <thead>
              <tr className={Styles.archiveTable}>
                <th>Counterparty</th>
                <th>Injection/Drawal Range</th>
                <th>Banking Factor</th>
                <th>Settlement Rate</th>
              </tr>
            </thead>
          </table>

          {/* Table Content */}
          {loading ? (
            <div className={Styles.noRecords}>Loading...</div>
          ) : currentRows.length > 0 ? (
            currentRows.map((elem, elemIndex) => {
              const selfData = elem?.selfListing;
              const counterData = elem?.counterPartyListings;

              if (!counterData || counterData.length === 0) return null;

              return counterData.map((row, index) => (
                <div
                  key={`${elemIndex}-${index}`}
                  className={`${Styles.tableRow} ${isPlacedTab ? Styles.placedTableRow : ""}`}
                >
                  <table
                    className={Styles.summaryTable}
                    style={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <colgroup>
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "25%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "15%" }} />
                    </colgroup>
                    <tbody>
                      <tr
                        className={`${isPlacedTab ? Styles.placedTableContent : ""}`}
                      >
                        <td
                          className={`${Styles.summaryCell} ${isPlacedTab ? Styles.placedTableContent : ""}`}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 400,
                                fontSize: "12px",
                                color: "var(--sixth-color-text)",
                              }}
                            >
                              {row.companyName || "-"}
                            </span>
                            <span
                              style={{
                                fontWeight: 400,
                                fontSize: "10px",
                                color: "var(--fifth-color-text)",
                              }}
                            >
                              {row.name || "-"}
                            </span>
                          </div>
                        </td>
                        <td
                          className={`${Styles.summaryViewCell} ${isPlacedTab ? Styles.placedTableContent : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenView(row);
                          }}
                        >
                          View All
                        </td>
                        <td
                          className={`${Styles.summaryCell} ${isPlacedTab ? Styles.placedTableContent : ""}`}
                        >
                          {row.bankingFactor
                            ? Number(row.bankingFactor).toFixed(2)
                            : "-%"}
                        </td>
                        <td
                          className={`${Styles.summaryCell} ${isPlacedTab ? Styles.placedTableContent : ""}`}
                        >
                          {row.settlementRate || "- (₹/kWh)"}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Action Buttons */}
                  <div
                    className={`${Styles.buttonSection} ${isPlacedTab ? Styles.placedButtons : ""}`}
                  >
                    {activeTab === "Completed" && (
                      <div className={Styles.buttonLeftButton}>
                        <button
                          className={Styles.actionButton}
                          onClick={() => {
                            setHistoryConnectionId(row?.connectionId);
                            setConnectionHistoryOpen(true);
                          }}
                        >
                          <HistoryIcon className={Styles.icon} />
                          <span>Connection History</span>
                          <VectorIcon className={Styles.iconSmall} />
                        </button>
                        <button
                          className={Styles.actionButton}
                          onClick={() => {
                            setSelectedConnection(elem);
                            setSelectedCounterparty(row);
                            setModalViewOnly(true);
                            setModalApprovalMode(false);
                            setFinalModal(true);
                          }}
                        >
                          <span>View Finalized T&C</span>
                          <VectorIcon className={Styles.iconSmall} />
                        </button>
                        <button
                          className={Styles.actionButton}
                          onClick={() => {
                            setViewDocConnectionId(row?.connectionId);
                            setActiveViewCounterparty(row);
                            setViewDocModalOpen(true);
                          }}
                        >
                          <span>View Doc</span>
                          <VectorIcon className={Styles.iconSmall} />
                        </button>
                        <button
                          className={Styles.actionButton}
                          onClick={() => {
                            setLedgerConnectionId(row?.connectionId);
                            setActiveViewCounterparty(row);
                            setLedgerModalOpen(true);
                          }}
                        >
                          <span>View Ledger</span>
                          <VectorIcon className={Styles.iconSmall} />
                        </button>
                      </div>
                    )}
                    {!isWithdrawanView && (
                      <button
                        className={`${Styles.viewChatButton} ${isPlacedTab ? Styles.placedViewButton : ""}`}
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          handleChatOpen(
                            selfData?.name,
                            row?.name,
                            row?.connectionInitiatorListingId,
                          )
                        }
                      >
                        View Chats
                      </button>
                    )}
                  </div>
                </div>
              ));
            })
          ) : (
            <div className={Styles.noRecords}>{getNoRecordsMessage()}</div>
          )}
        </div>
      </div>
    </>
  );
};

export default ArchiveModal;
